// Phase P.35 — Finance / Payments-received reconciliation ledger.
//
// Manager-only page. Every real inflow (invoice paid, cash intake, staff
// or manager settlement, manual entry) shows up here so the manager can
// tick reconciled ✓ against their bank statement, drop a ref, and note
// anything odd. Discrepancies are called out in red on the row + rolled
// up in the summary cards.

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Banknote, Plus, Search, Trash2, Check, StickyNote, AlertCircle,
  RefreshCw, X, Upload,
} from "lucide-react";
import { formatBookingStamp } from "@/lib/dates";
import BankCsvImportDialog from "@/components/BankCsvImportDialog";

const SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "invoice", label: "Invoice paid" },
  { value: "cash_intake", label: "Cash intake" },
  { value: "manager_settlement", label: "Settlement" },
  { value: "manual", label: "Manual entry" },
];

const SOURCE_LABELS = {
  invoice: "Invoice",
  cash_intake: "Cash intake",
  manager_settlement: "Settlement",
  manual: "Manual",
};

function Loader2Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3a9 9 0 1 0 9 9" strokeLinecap="round" />
    </svg>
  );
}

function AddPaymentDialog({ open, onOpenChange, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    source_label: "",
    customer_name: "",
    expected_amount: "",
    received_amount: "",
    received_at: today,
    statement_ref: "",
    note: "",
    reconciled: false,
  });
  const [busy, setBusy] = useState(false);
  // Phase P.39 — invoice lookup state.
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [lookupQ, setLookupQ] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [paidMethods, setPaidMethods] = useState([]);
  const [paidMethodCode, setPaidMethodCode] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        source_label: "", customer_name: "",
        expected_amount: "", received_amount: "",
        received_at: today, statement_ref: "", note: "",
        reconciled: false,
      });
      setLinkedInvoice(null);
      setLookupQ("");
      setLookupResults([]);
      setLookupOpen(false);
      setPaidMethodCode("");
      // Load paid methods once per open so the dropdown is fresh.
      api.get("/settings/paid-methods")
        .then((r) => setPaidMethods(r.data || []))
        .catch(() => setPaidMethods([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced invoice lookup — fires when there's a query AND no invoice
  // is already locked in.
  useEffect(() => {
    if (!open || linkedInvoice) return;
    const q = (lookupQ || "").trim();
    if (!q) { setLookupResults([]); setLookupOpen(false); return; }
    let cancelled = false;
    setLookupBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/finance/invoice-lookup", { params: { q, limit: 15 } });
        if (!cancelled) {
          setLookupResults(r.data || []);
          setLookupOpen(true);
        }
      } catch (e) {
        if (!cancelled) { setLookupResults([]); setLookupOpen(false); }
      } finally {
        if (!cancelled) setLookupBusy(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [lookupQ, open, linkedInvoice]);

  const selectInvoice = (inv) => {
    setLinkedInvoice(inv);
    setLookupOpen(false);
    setLookupResults([]);
    setLookupQ(`${inv.invoice_number} — ${inv.customer_company || inv.customer_contact || ""}`);
    // Auto-fill the form fields from the invoice.
    setForm((f) => ({
      ...f,
      source_label: `Invoice ${inv.invoice_number}`,
      customer_name: inv.customer_company || inv.customer_contact || f.customer_name,
      expected_amount: String(inv.total || ""),
      received_amount: String(inv.total || ""),
      statement_ref: f.statement_ref || inv.invoice_number,
    }));
  };

  const clearInvoiceLink = () => {
    setLinkedInvoice(null);
    setLookupQ("");
    setLookupResults([]);
    setPaidMethodCode("");
    setForm((f) => ({
      ...f, source_label: "", expected_amount: "", received_amount: "",
      statement_ref: "",
    }));
  };

  const save = async () => {
    const exp = Number(form.expected_amount || 0);
    const rec = Number(form.received_amount || 0);
    if (rec <= 0) { toast.error("Received amount must be > 0"); return; }
    if (linkedInvoice && !paidMethodCode) {
      toast.error("Pick how the customer paid you"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/finance/payments", {
        ...form,
        invoice_id: linkedInvoice?.id || undefined,
        paid_method_code: linkedInvoice ? paidMethodCode : undefined,
        expected_amount: exp || rec,
        received_amount: rec,
        received_at: form.received_at ? new Date(form.received_at + "T00:00:00Z").toISOString() : null,
      });
      toast.success(linkedInvoice ? `Invoice ${linkedInvoice.invoice_number} marked paid` : "Payment recorded");
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="finance-add-dialog">
        <DialogHeader>
          <DialogTitle>Record a payment</DialogTitle>
          <DialogDescription>Link it to an unpaid invoice, or record a manual entry for anything else.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Phase P.39 — invoice lookup / link */}
          <div className="border border-border rounded-[8px] p-3 bg-muted/30" data-testid="finance-add-invoice-lookup">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Look up unpaid invoice</Label>
            {linkedInvoice ? (
              <div className="mt-2 flex items-center gap-2 justify-between rounded-[6px] border border-emerald-300 bg-emerald-50 px-3 py-2" data-testid="finance-add-invoice-selected">
                <div className="text-sm">
                  <div className="font-semibold text-emerald-900">{linkedInvoice.invoice_number} · {formatCurrency(linkedInvoice.total, linkedInvoice.currency)}</div>
                  <div className="text-xs text-emerald-800">{linkedInvoice.customer_company || linkedInvoice.customer_contact || "—"}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={clearInvoiceLink} data-testid="finance-add-invoice-clear">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative mt-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    value={lookupQ}
                    onChange={(e) => setLookupQ(e.target.value)}
                    placeholder="Invoice number, customer, or amount…"
                    className="pl-8"
                    data-testid="finance-add-invoice-search"
                    autoComplete="off"
                  />
                </div>
                {lookupOpen && (
                  <div className="absolute z-50 mt-1 w-full rounded-[8px] border border-border bg-popover shadow-lg max-h-64 overflow-y-auto" data-testid="finance-add-invoice-results">
                    {lookupBusy ? (
                      <div className="p-3 text-sm text-muted-foreground">Searching…</div>
                    ) : lookupResults.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground" data-testid="finance-add-invoice-empty">No matching unpaid invoices</div>
                    ) : (
                      lookupResults.map((inv) => (
                        <button
                          key={inv.id}
                          type="button"
                          onClick={() => selectInvoice(inv)}
                          className="w-full text-left px-3 py-2 hover:bg-muted/60 border-b border-border/60 last:border-b-0"
                          data-testid={`finance-add-invoice-option-${inv.invoice_number}`}
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="font-semibold text-sm">{inv.invoice_number}</div>
                            <div className="mono text-sm tabular-nums font-bold">{formatCurrency(inv.total, inv.currency)}</div>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {inv.customer_company || inv.customer_contact || "—"}
                            {inv.payment_status && inv.payment_status !== "awaiting_payment" ? ` · ${inv.payment_status.replace(/_/g,' ')}` : ""}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {linkedInvoice && (
              <div className="mt-3">
                <Label className="text-xs">Paid via</Label>
                <Select value={paidMethodCode} onValueChange={setPaidMethodCode}>
                  <SelectTrigger className="mt-1" data-testid="finance-add-invoice-paid-method">
                    <SelectValue placeholder="How did they pay?" />
                  </SelectTrigger>
                  <SelectContent>
                    {paidMethods.map((pm) => (
                      <SelectItem key={pm.code} value={pm.code} data-testid={`finance-add-paid-method-${pm.code}`}>
                        {pm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Source label</Label>
            <Input
              value={form.source_label}
              onChange={(e) => setForm({ ...form, source_label: e.target.value })}
              placeholder="e.g. Bank transfer, Cheque #4421"
              data-testid="finance-add-source-label"
              disabled={!!linkedInvoice}
            />
          </div>
          <div>
            <Label className="text-xs">Customer / from</Label>
            <Input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Who paid you?"
              data-testid="finance-add-customer"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Expected €</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.expected_amount}
                onChange={(e) => setForm({ ...form, expected_amount: e.target.value })}
                data-testid="finance-add-expected"
              />
            </div>
            <div>
              <Label className="text-xs">Received €</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.received_amount}
                onChange={(e) => setForm({ ...form, received_amount: e.target.value })}
                data-testid="finance-add-received"
              />
            </div>
            <div>
              <Label className="text-xs">Received on</Label>
              <Input
                type="date"
                value={form.received_at}
                onChange={(e) => setForm({ ...form, received_at: e.target.value })}
                data-testid="finance-add-date"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Statement ref</Label>
            <Input
              value={form.statement_ref}
              onChange={(e) => setForm({ ...form, statement_ref: e.target.value })}
              placeholder="Bank statement reference"
              data-testid="finance-add-ref"
            />
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="finance-add-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-cta" onClick={save} disabled={busy} data-testid="finance-add-save">
            {busy ? "Saving…" : linkedInvoice ? `Mark ${linkedInvoice.invoice_number} paid` : "Save payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotePopover({ row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(row.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/finance/payments/${row.id}`, { note });
      onSaved?.(r.data);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={
            "inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border " +
            (row.note ? "border-amber-300 bg-amber-50 text-amber-800" : "border-border text-muted-foreground hover:bg-muted")
          }
          data-testid={`finance-row-${row.id}-note-btn`}
        >
          <StickyNote className="h-3 w-3" />
          {row.note ? "Note" : "Add note"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" data-testid={`finance-row-${row.id}-note-popover`}>
        <div className="space-y-2">
          <Label className="text-xs">Note</Label>
          <textarea
            className="w-full min-h-[80px] rounded-md border border-border bg-background text-sm p-2"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy}>Save</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function InlineText({ value, onSave, placeholder = "", testId }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-left w-full text-xs px-2 py-1 rounded hover:bg-muted min-h-[24px]"
        data-testid={testId}
      >
        {value || <span className="text-muted-foreground italic">{placeholder}</span>}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { onSave(v); setEditing(false); }
          if (e.key === "Escape") { setV(value || ""); setEditing(false); }
        }}
        className="text-xs px-2 py-1 rounded border border-border bg-background w-full"
      />
      <Check className="h-3 w-3 text-emerald-600 cursor-pointer" onClick={() => { onSave(v); setEditing(false); }} />
      <X className="h-3 w-3 text-muted-foreground cursor-pointer" onClick={() => { setV(value || ""); setEditing(false); }} />
    </div>
  );
}

export default function Finance() {
  // Phase P.39 P0 — default date range widened from "first-of-current-month"
  // to "no from-filter" so historical mark-paid invoices show up right after
  // the backfill migration lands. Managers can narrow with the filter row
  // whenever they need a monthly view.
  const today = new Date();
  const [filters, setFilters] = useState({
    from: "", to: today.toISOString().slice(0, 10),
    source_type: "all", reconciled: "all", q: "",
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Phase P.36 — bank CSV import.
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {
      from: filters.from || undefined,
      to: filters.to || undefined,
      source_type: filters.source_type !== "all" ? filters.source_type : undefined,
      reconciled: filters.reconciled === "all" ? undefined : filters.reconciled === "yes",
      q: filters.q || undefined,
    };
    try {
      const [rL, rS] = await Promise.all([
        api.get("/finance/payments", { params }),
        api.get("/finance/summary", { params: { from: params.from, to: params.to } }),
      ]);
      setRows(rL.data || []);
      setSummary(rS.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const patchRow = async (row, patch) => {
    try {
      const r = await api.patch(`/finance/payments/${row.id}`, patch);
      setRows((prev) => prev.map((x) => x.id === row.id ? r.data : x));
      // Also refresh summary since counts / totals may have shifted.
      const params = {
        from: filters.from || undefined,
        to: filters.to || undefined,
      };
      const rS = await api.get("/finance/summary", { params });
      setSummary(rS.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/finance/payments/${deleteTarget.id}`);
      toast.success("Payment removed");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const hasDiscrepancy = (r) => Math.abs(Number(r.discrepancy || 0)) > 0.005;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">// Finance</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-1 flex items-center gap-2">
            <Banknote className="text-emerald-600 h-8 w-8" /> Payments received
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Reconcile every inflow against your bank statement. Invoices, cash intakes and settlements land here automatically; use "Add payment" for anything else.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="gap-2" data-testid="finance-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-background text-sm font-semibold cursor-pointer hover:bg-muted whitespace-nowrap" data-testid="finance-import-btn">
            {importing ? <Loader2Spinner /> : <Upload className="h-4 w-4" />}
            {importing ? "Uploading…" : "Import bank CSV"}
            <input
              type="file" accept=".csv,text/csv"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                setImporting(true);
                try {
                  const fd = new FormData();
                  fd.append("file", f);
                  const r = await api.post("/finance/import-bank-csv", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  setImportPreview(r.data);
                  setImportOpen(true);
                } catch (err) {
                  toast.error(formatApiError(err?.response?.data?.detail) || "Import failed");
                } finally {
                  setImporting(false);
                }
              }}
            />
          </label>
          <Button className="btn-cta gap-2" onClick={() => setAddOpen(true)} data-testid="finance-add-btn">
            <Plus className="h-4 w-4" /> Add payment
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-border p-4 bg-background">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Expected</div>
          <div className="text-2xl font-black tabular-nums mono mt-1" data-testid="finance-total-expected">
            {formatCurrency(summary?.total_expected || 0, "EUR")}
          </div>
        </div>
        <div className="rounded-lg border border-emerald-200 p-4 bg-emerald-50">
          <div className="mono text-[10px] uppercase tracking-widest text-emerald-800">Received</div>
          <div className="text-2xl font-black tabular-nums mono mt-1 text-emerald-900" data-testid="finance-total-received">
            {formatCurrency(summary?.total_received || 0, "EUR")}
          </div>
        </div>
        <div className={`rounded-lg border p-4 ${Math.abs(summary?.total_discrepancy || 0) > 0.005 ? "border-rose-200 bg-rose-50" : "border-border bg-background"}`}>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Discrepancy</div>
          <div className={`text-2xl font-black tabular-nums mono mt-1 ${Math.abs(summary?.total_discrepancy || 0) > 0.005 ? "text-rose-700" : ""}`} data-testid="finance-total-discrepancy">
            {formatCurrency(summary?.total_discrepancy || 0, "EUR")}
          </div>
        </div>
        <div className="rounded-lg border border-border p-4 bg-background">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Reconciled</div>
          <div className="text-2xl font-black tabular-nums mono mt-1" data-testid="finance-count-reconciled">
            {summary?.count_reconciled || 0}<span className="text-muted-foreground text-base font-normal"> / {summary?.count_total || 0}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 border border-border rounded-lg p-3">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="w-40" data-testid="finance-filter-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="w-40" data-testid="finance-filter-to" />
        </div>
        <div>
          <Label className="text-xs">Source</Label>
          <Select value={filters.source_type} onValueChange={(v) => setFilters({ ...filters, source_type: v })}>
            <SelectTrigger className="w-44" data-testid="finance-filter-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Reconciled</Label>
          <Select value={filters.reconciled} onValueChange={(v) => setFilters({ ...filters, reconciled: v })}>
            <SelectTrigger className="w-40" data-testid="finance-filter-reconciled"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Reconciled ✓</SelectItem>
              <SelectItem value="no">Unreconciled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Ref, note, customer…" className="pl-7" data-testid="finance-filter-search" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left mono text-[11px] uppercase tracking-widest text-muted-foreground">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Customer / from</th>
                <th className="px-3 py-2 text-right">Expected</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2">Statement ref</th>
                <th className="px-3 py-2">Note</th>
                <th className="px-3 py-2 text-center">Reconciled</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No payments in this range. Mark an invoice paid or add a manual entry.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className={hasDiscrepancy(r) ? "bg-rose-50" : ""} data-testid={`finance-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs mono">
                    {r.received_at ? formatBookingStamp(r.received_at) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-muted text-[10px] mono uppercase tracking-widest">
                      {SOURCE_LABELS[r.source_type] || r.source_type}
                    </span>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-[220px]" title={r.source_label}>
                      {r.source_label}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs truncate max-w-[180px]">{r.customer_name || "—"}</td>
                  <td className="px-3 py-2 text-right mono tabular-nums">{formatCurrency(r.expected_amount, r.currency)}</td>
                  <td className={`px-3 py-2 text-right mono tabular-nums font-bold ${hasDiscrepancy(r) ? "text-rose-700" : ""}`}>
                    <InlineText
                      value={r.received_amount.toFixed(2)}
                      onSave={(v) => {
                        const n = Number(v);
                        if (!Number.isFinite(n)) { toast.error("Bad number"); return; }
                        patchRow(r, { received_amount: n });
                      }}
                      testId={`finance-row-${r.id}-received`}
                    />
                    {hasDiscrepancy(r) && (
                      <div className="text-[10px] mono uppercase tracking-widest text-rose-600 flex items-center justify-end gap-1 mt-0.5">
                        <AlertCircle className="h-3 w-3" /> Δ {formatCurrency(r.discrepancy, r.currency)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 min-w-[140px]">
                    <InlineText
                      value={r.statement_ref}
                      placeholder="Add ref…"
                      onSave={(v) => patchRow(r, { statement_ref: v })}
                      testId={`finance-row-${r.id}-ref`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <NotePopover row={r} onSaved={(fresh) => setRows((prev) => prev.map((x) => x.id === fresh.id ? fresh : x))} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!r.reconciled}
                      onChange={(e) => patchRow(r, { reconciled: e.target.checked })}
                      className="h-4 w-4 accent-emerald-600 cursor-pointer"
                      data-testid={`finance-row-${r.id}-reconciled`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setDeleteTarget(r)}
                      className="text-rose-700 hover:bg-rose-50 rounded p-1"
                      data-testid={`finance-row-${r.id}-delete`}
                      title="Delete payment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AddPaymentDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />

      <BankCsvImportDialog
        open={importOpen}
        onOpenChange={(o) => { setImportOpen(o); if (!o) setImportPreview(null); }}
        preview={importPreview}
        onApplied={load}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this payment record?</AlertDialogTitle>
            <AlertDialogDescription>
              The row will be soft-deleted (audit-preserving). If this came from an invoice or settlement,
              marking the source unpaid/undoing the settlement is a better path.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
